import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { ReportHeader, ReportRenderer } from '../components/reports';
import { ExternalLink, AlertCircle } from 'lucide-react';
import { convertLatexToUnicode } from '../components/reports/latexUtils';

const API_URL = '/api';

export const ExportReport = () => {
    const { hash } = useParams();
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedCategories, setExpandedCategories] = useState(new Set());

    useEffect(() => {
        loadReport();
    }, [hash]);

    const loadReport = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await axios.get(`${API_URL}/export/${hash}`);
            setReport(response.data);
        } catch (err) {
            console.error('Error loading shared report:', err);
            if (err.response?.status === 404) {
                setError('공유 링크를 찾을 수 없습니다.');
            } else if (err.response?.status === 410) {
                setError(err.response.data.detail || '공유 링크가 만료되었거나 비활성화되었습니다.');
            } else {
                setError('리포트를 불러오는 중 오류가 발생했습니다.');
            }
        } finally {
            setLoading(false);
        }
    };

    const toggleCategory = (reportId, categoryIdx, event) => {
        event.stopPropagation();
        const key = `${reportId}-${categoryIdx}`;
        setExpandedCategories(prev => {
            const newSet = new Set(prev);
            if (newSet.has(key)) {
                newSet.delete(key);
            } else {
                newSet.add(key);
            }
            return newSet;
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">리포트를 불러오는 중입니다...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <Card className="max-w-md w-full mx-4">
                    <CardContent className="pt-6">
                        <div className="text-center">
                            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                            <h2 className="text-xl font-semibold mb-2">리포트를 불러올 수 없습니다</h2>
                            <p className="text-gray-600 mb-4">{error}</p>
                            <a
                                href="/"
                                className="inline-flex items-center text-blue-600 hover:text-blue-700"
                            >
                                메인 페이지로 이동
                                <ExternalLink className="ml-1 w-4 h-4" />
                            </a>
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (!report) {
        return null;
    }

    return (
        <div className="min-h-screen bg-gray-50">
            {/* 간소화된 헤더 */}
            <header className="bg-white shadow-sm border-b">
                <div className="container mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-xl font-semibold text-gray-900">공유된 리포트</h1>
                            <p className="text-sm text-gray-500 mt-1">
                                PaperInsight에서 공유된 리포트입니다
                            </p>
                        </div>
                        <a
                            href="/"
                            className="text-sm text-blue-600 hover:text-blue-700 flex items-center"
                        >
                            PaperInsight 바로가기
                            <ExternalLink className="ml-1 w-4 h-4" />
                        </a>
                    </div>
                </div>
            </header>

            {/* 리포트 내용 */}
            <div className="container mx-auto px-4 py-8">
                <Card className="mb-6">
                    <ReportHeader
                        report={report}
                        isExpanded={true}
                    />

                    <CardContent>
                        {/* 논문 정보가 있는 경우 상단에 표시 */}
                        {report.report_content.title && (
                            <div className="mb-4 p-3 bg-gray-50 rounded-md">
                                <h3 className="font-semibold text-lg mb-2">
                                    {convertLatexToUnicode(report.report_content.title)}
                                </h3>
                                {report.report_content.arxiv_id && (
                                    <a
                                        href={`https://arxiv.org/abs/${report.report_content.arxiv_id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-sm mb-2"
                                    >
                                        arXiv: {report.report_content.arxiv_id}
                                        <ExternalLink className="w-3 h-3" />
                                    </a>
                                )}
                                {/* Easy Read 원본 URL 링크 */}
                                {report.report_content.source_type && (
                                    (() => {
                                        const sourceType = report.report_content.source_type;
                                        const sourceId = report.report_content.source_id;
                                        let originalUrl = report.report_content.original_url;
                                        let label = '원본 보기';

                                        // original_url이 없으면 직접 구성
                                        if (!originalUrl && sourceId) {
                                            if (sourceType === 'youtube') {
                                                originalUrl = `https://www.youtube.com/watch?v=${sourceId}`;
                                            } else if (sourceType === 'paper') {
                                                originalUrl = `https://arxiv.org/abs/${sourceId}`;
                                            }
                                        }

                                        // 라벨 설정
                                        if (sourceType === 'youtube') {
                                            label = '원본 영상 보기';
                                        } else if (sourceType === 'article') {
                                            label = '원본 기사 보기';
                                        } else if (sourceType === 'paper') {
                                            label = '원본 논문 보기';
                                        }

                                        return originalUrl ? (
                                            <a
                                                href={originalUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-blue-600 hover:text-blue-700 flex items-center gap-1 text-sm mb-2"
                                            >
                                                {label}
                                                <ExternalLink className="w-3 h-3" />
                                            </a>
                                        ) : null;
                                    })()
                                )}
                                {report.authors && report.authors.length > 0 && (
                                    <p className="text-sm text-gray-600">
                                        저자: {report.authors.join(', ')}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* 리포트 렌더러 */}
                        <ReportRenderer
                            report={report}
                            reportId={report.id}
                            expandedCategories={expandedCategories}
                            onToggleCategory={toggleCategory}
                        />
                    </CardContent>
                </Card>

                {/* Footer */}
                <div className="text-center text-sm text-gray-500 mt-8">
                    <p>이 리포트는 PaperInsight에서 생성되었습니다</p>
                    <a
                        href="/"
                        className="text-blue-600 hover:text-blue-700 mt-2 inline-block"
                    >
                        PaperInsight 둘러보기
                    </a>
                </div>
            </div>
        </div>
    );
};
